import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { chainIdToChainName } from '@shared/Enums'
import { EthAddr } from '@shared/SchemaTypes'
import { PoolContractCTO } from '@shared/sharedTypes'
import React, { useEffect, useState } from 'react'

// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.
export default function ReportVaultMultiSelect(props: {
  vaultCTOs: Record<EthAddr, PoolContractCTO>
  onSelectedChange: (selectedPoolAddrs: EthAddr[]) => void
  multiple: boolean
}): React.ReactElement {
  const [selectedPoolAddrs, setSelectedPoolAddrs] = useState<EthAddr[]>([])

  useEffect(() => {
    if (Object.keys(props.vaultCTOs).length > 0) {
      const _poolAddrsString = localStorage.getItem('reportPoolAddr') as EthAddr | undefined
      if (_poolAddrsString) {
        const _poolAddrs = _poolAddrsString.split(',').filter((_poolAddr) => _poolAddr.trim() !== '')
        const _selectedPoolAddrs = _poolAddrs.filter(
          (_poolAddr) => _poolAddrs.includes(_poolAddr) && props.vaultCTOs[_poolAddr]
        )
        if (_selectedPoolAddrs.length !== selectedPoolAddrs.length) {
          setSelectedPoolAddrs(_selectedPoolAddrs)
          props.onSelectedChange(_selectedPoolAddrs)
        }
      } else {
        setSelectedPoolAddrs([])
        props.onSelectedChange([])
      }
    }
  }, [props.vaultCTOs])

  const onChangeHandler = (event: SelectChangeEvent<EthAddr[]>) => {
    const {
      target: { value },
    } = event
    const _selectedPoolAddrs =
      typeof value === 'string' ? value.split(',').filter((_poolAddr) => _poolAddr.trim() !== '') : value
    setSelectedPoolAddrs(_selectedPoolAddrs)

    localStorage.setItem('reportPoolAddr', _selectedPoolAddrs.join(','))
    props.onSelectedChange(_selectedPoolAddrs)
  }
  const poolAddrCompare = (a: EthAddr, b: EthAddr) => {
    const vaultCTOa = props.vaultCTOs[a]
    const vaultCTOb = props.vaultCTOs[b]
    return vaultCTOa.displayName.localeCompare(vaultCTOb.displayName)
  }

  return (
    <FormControl sx={{ m: 1, width: 450, padding: '15px' }} size="medium">
      <InputLabel id="vault-label">Vaults</InputLabel>
      <Select
        size="medium"
        multiple={props.multiple}
        style={{ width: 350 }}
        labelId="vault-label"
        id="value-select"
        value={selectedPoolAddrs}
        renderValue={(selected: EthAddr[]) => {
          let poolListString = selected.map((poolAddr) => props.vaultCTOs[poolAddr].displayName).join(', ')
          if (poolListString.length > 30) {
            poolListString = poolListString.substring(0, 30) + '...'
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '32px', pl: 2 }}>
              <Typography variant="bodyBold">{poolListString}</Typography>
            </Box>
          )
        }}
        onChange={onChangeHandler}
      >
        {Object.keys(props.vaultCTOs)
          .sort(poolAddrCompare)
          .map((poolAddr) => {
            const vaultCTO = props.vaultCTOs[poolAddr]

            return (
              <MenuItem
                value={poolAddr}
                key={poolAddr}
                style={{
                  paddingLeft: 12,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}
              >
                <Checkbox checked={selectedPoolAddrs.includes(poolAddr)} />
                <ListItemText primary={vaultCTO.displayName} secondary={chainIdToChainName(vaultCTO.chainId)} />
              </MenuItem>
            )
          })}
      </Select>
    </FormControl>
  )
}
